import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Eye, EyeOff } from '../components/CustomIcons';
import LogoImg from '../components/LogoImg';

// ── Constants ─────────────────────────────────────────────────────────────────

const ROLES = [
  { value: 'learner',           label: 'Learner' },
  { value: 'facility_admin',    label: 'Facility Administrator' },
  { value: 'employer_partner',  label: 'Employer Partner' },
  { value: 'case_manager',      label: 'Case Manager' },
  { value: 'instructor',        label: 'Instructor' },
  { value: 'provider_partner',  label: 'Training Provider' },
];

const FACILITIES = [
  'North Texas Correctional Education Center',
  'Dallas County Jail',
  'Tarrant County Corrections',
  'Travis County Correctional Center',
  'Houston Metro Detention',
];

const CANONICAL_PATHWAYS = [
  'Electrical Technology',
  'CDL/Commercial Driving',
  'Cybersecurity',
  'Certified Nursing Assistant',
  'HVAC Technology',
  'Warehouse & Logistics',
  'Construction Management',
  'GED Preparation',
];

const CASELOAD_SIZES = ['1–10', '11–25', '26–50', '51–100', '100+'];
const EXPERIENCE_LEVELS = ['Less than 1 year', '1–2 years', '3–5 years', '6–10 years', '10+ years'];

// ── Shared style helpers ──────────────────────────────────────────────────────

const INPUT = {
  background: '#0D0D0D',
  border: '1px solid #333333',
  borderRadius: '10px',
  color: '#FFFFFF',
  padding: '12px 16px',
  width: '100%',
  fontFamily: 'Inter, sans-serif',
  fontSize: '15px',
  outline: 'none',
  boxSizing: 'border-box',
  minHeight: '44px',
};

const LABEL = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 600,
  color: '#A3A3A3',
  marginBottom: '6px',
  fontFamily: 'Inter, sans-serif',
};

const FIELD = { marginBottom: '14px' };

function focusHandlers(setBorder) {
  return {
    onFocus: e => (e.target.style.borderColor = '#F97316'),
    onBlur:  e => (e.target.style.borderColor = '#333333'),
  };
}
const FH = focusHandlers();

// ── Step indicator ────────────────────────────────────────────────────────────

function StepIndicator({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '20px' }}>
      {[1, 2].map((n, i) => (
        <React.Fragment key={n}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div
              aria-current={n === step ? 'step' : undefined}
              style={{
                width: '28px', height: '28px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '12px',
                background: n === step ? '#F97316' : n < step ? 'rgba(34,197,94,0.15)' : '#222222',
                color:      n === step ? '#111111' : n < step ? '#22C55E'              : '#666666',
                border:     n === step ? 'none'    : n < step ? '1px solid rgba(34,197,94,0.35)' : '1px solid #333333',
                flexShrink: 0,
              }}
            >
              {n}
            </div>
            <span style={{ fontSize: '12px', color: n === step ? '#FFFFFF' : '#666666', fontFamily: 'Inter, sans-serif' }}>
              {n === 1 ? 'Account' : 'Details'}
            </span>
          </div>
          {i === 0 && (
            <div style={{ width: '36px', height: '2px', borderRadius: '1px', background: step > 1 ? '#22C55E' : '#222222', flexShrink: 0 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Signup() {
  const [step, setStep] = useState(1);
  const [showPass, setShowPass] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '', role: 'learner',
    // Learner
    facility: '', doc_id: '', pathway_interest: '',
    // Facility Admin
    title_at_facility: '', department: '',
    // Case Manager
    facility_region: '', caseload_size: '',
    // Employer Partner
    company_name: '', industry: '', location: '',
    // Instructor
    pathways_taught: [], teaching_experience: '',
    // Training Provider
    org_name: '', accreditation_status: '', service_area: '',
    // Common
    terms: false,
  });
  const [step1Error, setStep1Error] = useState('');
  const [step2Error, setStep2Error] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const togglePathway = (p) =>
    setForm(f => ({
      ...f,
      pathways_taught: f.pathways_taught.includes(p)
        ? f.pathways_taught.filter(x => x !== p)
        : [...f.pathways_taught, p],
    }));

  // ── Step 1 validation ─────────────────────────────────────────────────────
  const handleStep1 = (e) => {
    e.preventDefault();
    setStep1Error('');
    if (!form.first_name.trim()) return setStep1Error('Please enter your first name.');
    if (!form.last_name.trim())  return setStep1Error('Please enter your last name.');
    if (!/\S+@\S+\.\S+/.test(form.email)) return setStep1Error('Please enter a valid email address.');
    if (form.password.length < 6) return setStep1Error('Password must be at least 6 characters.');
    setStep(2);
  };

  // ── Step 2 submission ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStep2Error('');
    if (!form.terms) return setStep2Error('Please accept the Terms of Service to continue.');
    setLoading(true);
    try {
      const user = await register(form);
      if      (user.role === 'facility_admin')   navigate('/admin');
      else if (user.role === 'case_manager')     navigate('/case-manager');
      else if (user.role === 'instructor')       navigate('/instructor');
      else if (user.role === 'employer_partner') navigate('/jobs/employer');
      else if (user.role === 'provider_partner') navigate('/marketplace/provider');
      else                                       navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setStep2Error(typeof detail === 'string' ? detail : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Role-specific fields (plain function — not a React component — to avoid remount) ──
  const renderRoleFields = () => {
    const sectionHeader = (title) => (
      <p style={{ fontSize: '12px', fontWeight: 600, color: '#666666', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Plus Jakarta Sans, sans-serif', marginBottom: '14px' }}>
        {title}
      </p>
    );

    switch (form.role) {

      case 'learner':
        return (
          <div>
            {sectionHeader('Learner Details (optional)')}
            <div style={FIELD}>
              <label htmlFor="su-facility" style={LABEL}>Facility</label>
              <select id="su-facility" value={form.facility} onChange={e => set('facility', e.target.value)} style={{ ...INPUT, cursor: 'pointer' }} data-testid="signup-facility" {...FH}>
                <option value="">Select facility…</option>
                {FACILITIES.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div style={FIELD}>
              <label htmlFor="su-pathway" style={LABEL}>Pathway Interest</label>
              <select id="su-pathway" value={form.pathway_interest} onChange={e => set('pathway_interest', e.target.value)} style={{ ...INPUT, cursor: 'pointer' }} data-testid="signup-pathway" {...FH}>
                <option value="">Select a pathway…</option>
                {CANONICAL_PATHWAYS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div style={FIELD}>
              <label htmlFor="su-doc-id" style={LABEL}>
                DOC ID <span style={{ fontWeight: 400, color: '#666666', fontSize: '12px' }}>(optional)</span>
              </label>
              <input id="su-doc-id" value={form.doc_id} onChange={e => set('doc_id', e.target.value)} placeholder="TX-XXXX-XXXX" style={INPUT} {...FH} />
            </div>
          </div>
        );

      case 'facility_admin':
        return (
          <div>
            {sectionHeader('Facility Administrator Details')}
            <div style={FIELD}>
              <label htmlFor="su-adm-facility" style={LABEL}>
                Facility <span aria-hidden="true" style={{ color: '#EF4444' }}>*</span>
              </label>
              <select id="su-adm-facility" value={form.facility} onChange={e => set('facility', e.target.value)} style={{ ...INPUT, cursor: 'pointer' }} data-testid="signup-admin-facility" {...FH}>
                <option value="">Select facility…</option>
                {FACILITIES.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div style={FIELD}>
              <label htmlFor="su-title" style={LABEL}>Title / Role at Facility</label>
              <input id="su-title" value={form.title_at_facility} onChange={e => set('title_at_facility', e.target.value)} placeholder="e.g., Deputy Warden, Education Director" style={INPUT} {...FH} />
            </div>
            <div style={FIELD}>
              <label htmlFor="su-dept" style={LABEL}>Department</label>
              <input id="su-dept" value={form.department} onChange={e => set('department', e.target.value)} placeholder="e.g., Education, Reentry Services" style={INPUT} {...FH} />
            </div>
          </div>
        );

      case 'case_manager':
        return (
          <div>
            {sectionHeader('Case Manager Details')}
            <div style={FIELD}>
              <label htmlFor="su-region" style={LABEL}>
                Facility or Region <span aria-hidden="true" style={{ color: '#EF4444' }}>*</span>
              </label>
              <input id="su-region" value={form.facility_region} onChange={e => set('facility_region', e.target.value)} placeholder="e.g., Dallas County Jail / North Texas" required style={INPUT} data-testid="signup-facility-region" {...FH} />
            </div>
            <div style={FIELD}>
              <label htmlFor="su-caseload" style={LABEL}>Approximate Caseload Size</label>
              <select id="su-caseload" value={form.caseload_size} onChange={e => set('caseload_size', e.target.value)} style={{ ...INPUT, cursor: 'pointer' }} data-testid="signup-caseload" {...FH}>
                <option value="">Select range…</option>
                {CASELOAD_SIZES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
        );

      case 'employer_partner':
        return (
          <div>
            {sectionHeader('Employer Details')}
            <div style={FIELD}>
              <label htmlFor="su-company" style={LABEL}>
                Company Name <span aria-hidden="true" style={{ color: '#EF4444' }}>*</span>
              </label>
              <input id="su-company" value={form.company_name} onChange={e => set('company_name', e.target.value)} placeholder="Your company name" required style={INPUT} data-testid="signup-company" {...FH} />
            </div>
            <div style={FIELD}>
              <label htmlFor="su-industry" style={LABEL}>Industry</label>
              <input id="su-industry" value={form.industry} onChange={e => set('industry', e.target.value)} placeholder="e.g., Construction, Healthcare" style={INPUT} {...FH} />
            </div>
            <div style={FIELD}>
              <label htmlFor="su-location" style={LABEL}>Location</label>
              <input id="su-location" value={form.location} onChange={e => set('location', e.target.value)} placeholder="City, TX" style={INPUT} {...FH} />
            </div>
          </div>
        );

      case 'instructor':
        return (
          <div>
            {sectionHeader('Instructor Details')}
            <div style={FIELD}>
              <label htmlFor="su-inst-facility" style={LABEL}>Facility</label>
              <select id="su-inst-facility" value={form.facility} onChange={e => set('facility', e.target.value)} style={{ ...INPUT, cursor: 'pointer' }} {...FH}>
                <option value="">Select facility…</option>
                {FACILITIES.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <fieldset style={{ border: '1px solid #333333', borderRadius: '10px', padding: '14px', marginBottom: '14px' }}>
              <legend style={{ ...LABEL, padding: '0 6px', marginBottom: '10px' }}>
                Subject / Pathway(s) Taught
              </legend>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {CANONICAL_PATHWAYS.map(p => {
                  const checked = form.pathways_taught.includes(p);
                  return (
                    <label
                      key={p}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '8px 10px', borderRadius: '8px', cursor: 'pointer',
                        border: `1px solid ${checked ? 'rgba(249,115,22,0.35)' : '#333333'}`,
                        background: checked ? 'rgba(249,115,22,0.08)' : '#111111',
                        fontSize: '13px', color: checked ? '#F97316' : '#A3A3A3',
                        fontFamily: 'Inter, sans-serif', transition: 'all 0.15s',
                        minHeight: '44px',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePathway(p)}
                        style={{ accentColor: '#F97316', width: '16px', height: '16px', flexShrink: 0, cursor: 'pointer' }}
                        aria-label={p}
                      />
                      {p}
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <div style={FIELD}>
              <label htmlFor="su-experience" style={LABEL}>Years Teaching / Training Experience</label>
              <select id="su-experience" value={form.teaching_experience} onChange={e => set('teaching_experience', e.target.value)} style={{ ...INPUT, cursor: 'pointer' }} {...FH}>
                <option value="">Select…</option>
                {EXPERIENCE_LEVELS.map(l => <option key={l}>{l}</option>)}
              </select>
            </div>
          </div>
        );

      case 'provider_partner':
        return (
          <div>
            {sectionHeader('Training Provider Details')}
            <div style={FIELD}>
              <label htmlFor="su-org" style={LABEL}>
                Organization Name <span aria-hidden="true" style={{ color: '#EF4444' }}>*</span>
              </label>
              <input id="su-org" value={form.org_name} onChange={e => set('org_name', e.target.value)} placeholder="e.g., Dallas College, Tech Institute" required style={INPUT} data-testid="signup-org-name" {...FH} />
            </div>
            <div style={FIELD}>
              <label htmlFor="su-accreditation" style={LABEL}>Accreditation / Approval Status</label>
              <input id="su-accreditation" value={form.accreditation_status} onChange={e => set('accreditation_status', e.target.value)} placeholder="e.g., SACSCOC Accredited, TDCJ Approved" style={INPUT} {...FH} />
            </div>
            <div style={FIELD}>
              <label htmlFor="su-service-area" style={LABEL}>Primary Service Area</label>
              <input id="su-service-area" value={form.service_area} onChange={e => set('service_area', e.target.value)} placeholder="e.g., North Texas, Statewide" style={INPUT} {...FH} />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0D0D0D',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      <div className="animate-fade-in" style={{ width: '100%', maxWidth: '500px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'inline-block' }}>
            <div style={{ background: '#111111', border: '1px solid #222222', borderRadius: '10px', padding: '8px 18px', display: 'inline-block' }}>
              <LogoImg forceDark height={44} alt="Assistiv OS" style={{ display: 'block' }} />
            </div>
          </Link>
          <p style={{ fontSize: '12px', color: '#8C8C8C', marginTop: '6px', fontFamily: 'Inter, sans-serif' }}>
            Earn credentials. Plan your reentry. Walk out ready.
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: '#1A1A1A',
          border: '1px solid #333333',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
          maxHeight: '88vh',
          overflowY: 'auto',
        }}>
          <StepIndicator step={step} />

          <div style={{ marginBottom: '20px' }}>
            <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '22px', color: '#FFFFFF', margin: '0 0 4px 0' }}>
              {step === 1 ? 'Create your account' : 'Tell us about yourself'}
            </h1>
            <p style={{ color: '#A3A3A3', fontSize: '14px', fontFamily: 'Inter, sans-serif', margin: 0 }}>
              {step === 1
                ? 'Start your path to a new career'
                : ROLES.find(r => r.value === form.role)?.label + ' details'}
            </p>
          </div>

          {/* ── STEP 1 ── */}
          {step === 1 && (
            <form onSubmit={handleStep1} noValidate>
              {step1Error && (
                <div role="alert" aria-live="polite" data-testid="signup-step1-error"
                  style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', color: '#EF4444', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
                  {step1Error}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                <div>
                  <label htmlFor="su-first" style={LABEL}>First Name</label>
                  <input id="su-first" value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="First name" required style={INPUT} data-testid="signup-first-name" autoComplete="given-name" {...FH} />
                </div>
                <div>
                  <label htmlFor="su-last" style={LABEL}>Last Name</label>
                  <input id="su-last" value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Last name" required style={INPUT} data-testid="signup-last-name" autoComplete="family-name" {...FH} />
                </div>
              </div>

              <div style={FIELD}>
                <label htmlFor="su-email" style={LABEL}>Email address</label>
                <input id="su-email" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@example.com" required aria-required="true" style={INPUT} data-testid="signup-email" autoComplete="email" {...FH} />
              </div>

              <div style={FIELD}>
                <label htmlFor="su-password" style={LABEL}>
                  Password{' '}
                  <span style={{ fontWeight: 400, color: '#666666', fontSize: '12px' }}>(min 6 characters)</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input id="su-password" type={showPass ? 'text' : 'password'} value={form.password} onChange={e => set('password', e.target.value)} placeholder="••••••••" required minLength={6} aria-required="true" style={{ ...INPUT, paddingRight: '44px' }} data-testid="signup-password" autoComplete="new-password" {...FH} />
                  <button type="button" onClick={() => setShowPass(p => !p)} aria-label={showPass ? 'Hide password' : 'Show password'}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#8C8C8C', padding: '4px', minHeight: '44px', display: 'flex', alignItems: 'center' }}>
                    {showPass ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label htmlFor="su-role" style={LABEL}>I am a…</label>
                <select id="su-role" value={form.role} onChange={e => set('role', e.target.value)} style={{ ...INPUT, cursor: 'pointer' }} data-testid="signup-role" {...FH}>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>

              <button type="submit" className="btn-primary" data-testid="signup-continue"
                style={{ width: '100%', justifyContent: 'center', fontSize: '15px', padding: '13px', minHeight: '44px' }}>
                Continue →
              </button>

              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <Link to="/login" style={{ color: '#F97316', fontSize: '14px', fontFamily: 'Inter, sans-serif', textDecoration: 'none' }}>
                  Already have an account?{' '}<span style={{ fontWeight: 600 }}>Sign In</span>
                </Link>
              </div>
            </form>
          )}

          {/* ── STEP 2 ── */}
          {step === 2 && (
            <form onSubmit={handleSubmit} noValidate>
              {step2Error && (
                <div role="alert" aria-live="polite" data-testid="signup-error"
                  style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', color: '#EF4444', fontSize: '13px', fontFamily: 'Inter, sans-serif' }}>
                  {step2Error}
                </div>
              )}

              {renderRoleFields()}

              {/* Terms */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '14px', background: '#111111', borderRadius: '10px', border: '1px solid #333333', marginBottom: '20px' }}>
                <input
                  type="checkbox"
                  id="su-terms"
                  checked={form.terms}
                  onChange={e => set('terms', e.target.checked)}
                  style={{ marginTop: '2px', accentColor: '#F97316', width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer' }}
                  data-testid="signup-terms"
                  aria-required="true"
                />
                <label htmlFor="su-terms" style={{ fontSize: '13px', color: '#A3A3A3', fontFamily: 'Inter, sans-serif', cursor: 'pointer', lineHeight: 1.55 }}>
                  I agree to the{' '}
                  <span style={{ color: '#F97316', fontWeight: 600 }}>Terms of Service</span>
                  {' '}and{' '}
                  <span style={{ color: '#F97316', fontWeight: 600 }}>Privacy Policy</span>
                </label>
              </div>

              <button type="submit" disabled={loading} className="btn-primary" data-testid="signup-submit"
                aria-busy={loading}
                style={{ width: '100%', justifyContent: 'center', fontSize: '15px', padding: '13px', opacity: loading ? 0.7 : 1, minHeight: '44px', marginBottom: '10px' }}>
                {loading ? 'Creating account…' : 'Create Account'}
              </button>

              <button
                type="button"
                onClick={() => { setStep2Error(''); setStep(1); }}
                data-testid="signup-back"
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  background: 'transparent', border: '1px solid #333333', borderRadius: '10px',
                  color: '#A3A3A3', fontSize: '14px', fontFamily: 'Inter, sans-serif',
                  padding: '11px', cursor: 'pointer', minHeight: '44px', transition: 'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#F97316'; e.currentTarget.style.color = '#F97316'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#333333'; e.currentTarget.style.color = '#A3A3A3'; }}
              >
                <ArrowLeft size={14} aria-hidden="true" /> Back to Step 1
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
